import {
  Box,
  Card,
  Group,
  Image,
  SegmentedControl,
  SimpleGrid,
  Slider,
  Stack,
  Switch,
  Tabs,
  Text,
  UnstyledButton,
} from '@mantine/core';
import type { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import PaletteColourField from 'src/secret-menu/brand-asset-builder/components/PaletteColourField';
import PreviewCanvas from 'src/secret-menu/brand-asset-builder/components/PreviewCanvas';
import { usePaletteColourDrafts } from 'src/secret-menu/brand-asset-builder/hooks/usePaletteColourDrafts';
import type {
  BrandAssetBuilderData,
  BrandAssetBuilderLogoPosition,
  BrandAssetBuilderPalette,
  BrandAssetBuilderShapeColour,
  BrandAssetBuilderTitlePageConfig,
  BrandAssetBuilderTitlePageTheme,
} from 'src/secret-menu/brand-asset-builder/types';

// Every new title-page theme (see spec/brand-asset-builder for the upcoming SVG-sourced themes)
// should be added here tagged with the category it fits best — the Templates tab groups by
// category automatically, and a category with no themes yet simply doesn't render, so there's no
// extra UI wiring needed as later PRs grow this list towards its full set of 26.
type ThemeCategory = 'flowing' | 'geometric' | 'minimal' | 'bold';

const THEME_CATEGORY_ORDER: ThemeCategory[] = ['flowing', 'geometric', 'minimal', 'bold'];

const THEME_CATEGORY_LABELS: Record<ThemeCategory, string> = {
  flowing: 'Flowing',
  geometric: 'Geometric',
  minimal: 'Minimal',
  bold: 'Bold',
};

const THEMES: {
  id: BrandAssetBuilderTitlePageTheme;
  name: string;
  description: string;
  category: ThemeCategory;
}[] = [
  {
    id: 'layered-wave',
    name: 'Layered wave',
    description: 'Broad rising waves across the lower-right',
    category: 'flowing',
  },
  {
    id: 'soft-horizon',
    name: 'Soft horizon',
    description: 'Smooth horizontal waves rising from the bottom',
    category: 'flowing',
  },
  {
    id: 'diagonal-sweep',
    name: 'Diagonal sweep',
    description: 'Bold layered diagonal bands',
    category: 'geometric',
  },
  {
    id: 'stepped-horizon',
    name: 'Stepped horizon',
    description: 'Three architectural steps across the lower edge',
    category: 'geometric',
  },
  {
    id: 'corner-swell',
    name: 'Corner swell',
    description: 'Smooth colour fields swelling from the corner',
    category: 'minimal',
  },
  {
    id: 'opposing-ribbons',
    name: 'Opposing ribbons',
    description: 'Layered ribbons entering from opposite corners',
    category: 'minimal',
  },
  {
    id: 'corner-cut',
    name: 'Corner cut',
    description: 'Restrained geometric cut rising from the corner',
    category: 'geometric',
  },
  {
    id: 'crescent-arc',
    name: 'Crescent arc',
    description: 'Layered sweeping crescents along the right and lower edges',
    category: 'flowing',
  },
];

const SHAPE_COLOUR_OPTIONS: { value: BrandAssetBuilderShapeColour; label: string }[] = [
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'accent', label: 'Accent' },
  { value: 'darkText', label: 'Dark text' },
];

const THEME_COLOUR_FIELDS: { key: keyof BrandAssetBuilderPalette; label: string }[] = [
  { key: 'primary', label: 'Primary' },
  { key: 'secondary', label: 'Secondary' },
  { key: 'accent', label: 'Accent' },
  { key: 'darkText', label: 'Dark text' },
];

const LOGO_POSITION_PRESET_X: Record<BrandAssetBuilderLogoPosition, number> = {
  left: 20,
  center: 50,
  right: 80,
};

function logoPositionFromX(logoX: number): BrandAssetBuilderLogoPosition | null {
  const preset = (
    Object.entries(LOGO_POSITION_PRESET_X) as [BrandAssetBuilderLogoPosition, number][]
  ).find(([, presetX]) => presetX === logoX);
  return preset ? preset[0] : null;
}

function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

type ThemeArtColours = {
  primary: string;
  accent: string;
  secondary: string;
  neutral: string;
};

const THEME_SHAPES: Record<
  BrandAssetBuilderTitlePageTheme,
  (colours: ThemeArtColours) => React.ReactNode
> = {
  'layered-wave': ({ primary, accent, secondary }) => (
    <>
      <path
        fill={secondary}
        d="M0 2860C508 2769 898 2552 1241 2201C1580 1854 1815 1568 2100 1072V2125C1800 2280 1581 2451 1363 2662C1235 2786 1112 2884 978 2970H0Z"
      />
      <path
        fill={primary}
        d="M723 2970C1116 2764 1401 2508 1617 2215C1824 1935 1961 1650 2100 1274V2970Z"
      />
      <path
        fill="none"
        stroke={accent}
        strokeWidth="7"
        strokeLinecap="round"
        d="M294 2970C814 2767 1214 2495 1519 2140"
      />
    </>
  ),
  'soft-horizon': ({ primary, secondary, neutral }) => (
    <>
      <path
        fill={neutral}
        d="M0 1880C322 1765 570 1772 845 1830C1148 1894 1421 2000 1702 1989C1850 1983 1983 1923 2100 1815V2970H0Z"
      />
      <path
        fill={primary}
        opacity="0.4"
        d="M0 2170C310 2060 596 2081 902 2153C1211 2226 1507 2291 1752 2190C1895 2131 2014 2022 2100 1919V2970H0Z"
      />
      <path
        fill={secondary}
        d="M0 2440C265 2362 535 2374 806 2440C1115 2516 1414 2582 1695 2474C1868 2407 2008 2276 2100 2160V2970H0Z"
      />
      <path
        fill={primary}
        d="M0 2695C281 2636 578 2651 882 2716C1190 2782 1501 2825 1783 2705C1912 2650 2018 2570 2100 2488V2970H0Z"
      />
    </>
  ),
  'diagonal-sweep': ({ primary, secondary, neutral }) => (
    <>
      <path fill={neutral} d="M0 2950L2100 1310V1830L715 2970H0Z" />
      <path fill={secondary} d="M715 2970L2100 1830V2315L1305 2970Z" />
      <path fill={primary} d="M1175 2970L2100 2208V2970Z" />
    </>
  ),
  'stepped-horizon': ({ primary, secondary, neutral }) => (
    <>
      <path fill={neutral} d="M0 2520H620V2320H1160V2110H1630V1880H2100V2970H0Z" />
      <path fill={secondary} d="M0 2710H755V2520H1305V2320H1740V2110H2100V2970H0Z" />
      <path
        fill={primary}
        fillOpacity=".55"
        d="M0 2870H905V2710H1450V2520H1840V2320H2100V2970H0Z"
      />
    </>
  ),
  'corner-swell': ({ primary, secondary, neutral }) => (
    <>
      <path
        fill={neutral}
        d="M1340 2970C965 2782 713 2534 560 2225C413 1929 246 1748 0 1650V2970Z"
      />
      <path
        fill={secondary}
        d="M890 2970C613 2783 439 2572 340 2320C245 2078 133 1927 0 1868V2970Z"
      />
      <path fill={primary} d="M495 2970C295 2801 188 2619 131 2412C89 2257 45 2165 0 2128V2970Z" />
    </>
  ),
  'opposing-ribbons': ({ primary, accent, secondary, neutral }) => (
    <>
      <path fill={neutral} d="M2100 0H730L2100 600Z" />
      <path fill={secondary} d="M2100 0H1205L2100 390Z" />
      <path fill={primary} d="M2100 0H1600L2100 220Z" />
      <path fill={accent} d="M2100 0H1895L2100 92Z" />
      <path fill={neutral} d="M1370 2970H0V2370Z" />
      <path fill={secondary} d="M895 2970H0V2580Z" />
      <path fill={primary} d="M500 2970H0V2750Z" />
      <path fill={accent} d="M205 2970H0V2878Z" />
    </>
  ),
  'corner-cut': ({ primary, secondary, neutral }) => (
    <>
      <path fill={neutral} d="M2100 1980L1390 2970H2100Z" />
      <path fill={secondary} d="M2100 2280L1605 2970H2100Z" />
      <path fill={primary} d="M2100 2580L1820 2970H2100Z" />
    </>
  ),
  'crescent-arc': ({ primary, accent, secondary, neutral }) => (
    <>
      <path
        fill={primary}
        d="M1780 0C1990 382 2100 810 2100 1285V2970H0V2800C481 2795 869 2694 1187 2495C1501 2298 1717 2017 1852 1654C1987 1292 2010 917 1922 561C1870 350 1805 163 1728 0Z"
      />
      <path
        fill={accent}
        fillOpacity=".62"
        d="M1932 0C2045 292 2100 615 2100 970V2970H1585C1791 2712 1930 2424 2002 2106C2080 1761 2081 1424 2025 1081C1971 750 1874 390 1748 0Z"
      />
      <path
        fill={neutral}
        d="M1708 0C1907 415 1954 856 1847 1277C1741 1695 1514 2034 1164 2293C837 2534 449 2654 0 2655V2785C470 2780 874 2654 1216 2406C1594 2132 1840 1770 1956 1320C2074 861 2015 405 1818 0Z"
      />
      <path
        fill={secondary}
        fillOpacity=".34"
        d="M1760 0C1952 415 1994 855 1882 1286C1771 1712 1538 2057 1180 2317C850 2556 455 2675 0 2676V2760C466 2757 865 2635 1201 2392C1573 2123 1815 1766 1929 1324C2047 866 1996 415 1803 0Z"
      />
      <path
        fill="#ffffff"
        d="M1862 0C2028 396 2058 816 1947 1220C1835 1628 1610 1965 1267 2225C943 2471 560 2603 105 2630L0 2635V2668C473 2663 875 2533 1213 2282C1577 2011 1818 1659 1938 1233C2057 810 2019 389 1856 0Z"
      />
    </>
  ),
};

type ThemeArtProps = {
  theme: BrandAssetBuilderTitlePageTheme;
  palette: BrandAssetBuilderPalette;
  shapeColor: BrandAssetBuilderShapeColour;
  size: number;
};

function ThemeArt({ theme, palette, shapeColor, size }: ThemeArtProps) {
  const baseColor = palette[shapeColor] || palette.primary;
  const primary = baseColor;
  const accent = shapeColor === 'accent' ? palette.primary : palette.accent;
  const secondary = hexToRgba(baseColor, 0.6);
  const neutral = hexToRgba(baseColor, 0.2);

  return (
    <svg
      viewBox="0 0 2100 2970"
      width={size}
      height={(size * 2970) / 2100}
      style={{ display: 'block' }}
    >
      <rect width={2100} height={2970} fill="#ffffff" />
      {THEME_SHAPES[theme]({ primary, accent, secondary, neutral })}
    </svg>
  );
}

export default function TitlePageStep(props: StepProps<BrandAssetBuilderData>) {
  const { data, setData } = props;
  const { palette, titlePageTheme, titlePageConfig, logoUrl, clientName, headingFont, bodyFont } =
    data;

  const {
    drafts: paletteDrafts,
    draftColour: draftPaletteColour,
    handleFocus: handleFieldFocus,
    handleBlur: handleFieldBlur,
  } = usePaletteColourDrafts(data.palette, (key, value) =>
    setData({ ...data, palette: { ...data.palette, [key]: value } }),
  );

  const updateConfig = <K extends keyof BrandAssetBuilderTitlePageConfig>(
    key: K,
    value: BrandAssetBuilderTitlePageConfig[K],
  ) => {
    setData({ ...data, titlePageConfig: { ...titlePageConfig, [key]: value } });
  };

  const today = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Design title page"
      description="Pick a title-page theme and adjust how the logo and shapes sit on the page."
      showClientName={false}
      hideProgress
      maxContainerWidth="lg"
    >
      <Group
        align="flex-start"
        gap="xl"
        wrap="nowrap"
        h="100%"
        style={{ alignItems: 'flex-start' }}
      >
        <Box
          style={{
            flexShrink: 0,
            width: 420,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box style={{ flex: 1, minHeight: 0 }}>
            <PreviewCanvas nativeWidth={320} nativeHeight={453}>
              <Card
                withBorder
                radius="md"
                p={0}
                style={{ width: 320, height: 453, overflow: 'hidden', position: 'relative' }}
              >
                <ThemeArt
                  theme={titlePageTheme}
                  palette={palette}
                  shapeColor={titlePageConfig.shapeColor}
                  size={320}
                />
                <Box
                  style={{
                    position: 'absolute',
                    left: `${titlePageConfig.logoX}%`,
                    top: `${titlePageConfig.logoY}%`,
                    transform: `translate(-50%, -50%) scale(${titlePageConfig.logoScale / 100})`,
                  }}
                >
                  {logoUrl ? (
                    <Image src={logoUrl} alt="Tenant logo" fit="contain" mah={55} maw={152} />
                  ) : (
                    <Box w={98} h={28} bg="gray.2" style={{ borderRadius: 4 }} />
                  )}
                </Box>
                <Stack
                  gap={2}
                  align="center"
                  style={{
                    position: 'absolute',
                    left: '10%',
                    right: '10%',
                    top: '46%',
                    transform: 'translateY(-50%)',
                  }}
                >
                  <Text
                    size="sm"
                    fw={FONT_WEIGHTS.bold}
                    c={palette.darkText}
                    ff={headingFont}
                    ta="center"
                  >
                    {clientName || 'Client name'}
                  </Text>
                  {!titlePageConfig.hideDate && (
                    <Text size="xs" c={palette.darkText} ff={bodyFont} tt="uppercase" ta="center">
                      {today}
                    </Text>
                  )}
                </Stack>
              </Card>
            </PreviewCanvas>
          </Box>
          <Text size="xs" c="gray.6" mt="xs" ta="center">
            Preview
          </Text>
        </Box>

        <Box flex={1} miw={0} h="100%" style={{ overflow: 'hidden' }}>
          <Tabs
            defaultValue="templates"
            style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
          >
            <Tabs.List>
              <Tabs.Tab value="templates">Templates</Tabs.Tab>
              <Tabs.Tab value="customise">Customise</Tabs.Tab>
            </Tabs.List>

            <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <Tabs.Panel value="templates" pt="md">
                <Stack gap="lg">
                  {THEME_CATEGORY_ORDER.map(category => {
                    const themesInCategory = THEMES.filter(theme => theme.category === category);
                    if (themesInCategory.length === 0) return null;

                    return (
                      <Stack key={category} gap="sm">
                        <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.7">
                          {THEME_CATEGORY_LABELS[category]}
                        </Text>
                        <SimpleGrid cols={{ base: 2 }} spacing="sm">
                          {themesInCategory.map(theme => {
                            const isSelected = titlePageTheme === theme.id;
                            return (
                              <UnstyledButton
                                key={theme.id}
                                onClick={() => setData({ ...data, titlePageTheme: theme.id })}
                              >
                                <Card
                                  withBorder
                                  radius="md"
                                  p="xs"
                                  bg={isSelected ? 'blue.0' : undefined}
                                  style={{
                                    borderColor: isSelected
                                      ? 'var(--mantine-color-blue-6)'
                                      : undefined,
                                    borderWidth: isSelected ? 2 : 1,
                                    height: '100%',
                                  }}
                                >
                                  <Stack gap="xs" align="center">
                                    <Card
                                      withBorder
                                      radius="sm"
                                      p={0}
                                      style={{ overflow: 'hidden' }}
                                    >
                                      <ThemeArt
                                        theme={theme.id}
                                        palette={palette}
                                        shapeColor={titlePageConfig.shapeColor}
                                        size={64}
                                      />
                                    </Card>
                                    <Stack gap={2} align="center">
                                      <Text size="xs" fw={FONT_WEIGHTS.semibold} ta="center">
                                        {theme.name}
                                      </Text>
                                      <Text size="xs" c="gray.6" ta="center" lh={1.2}>
                                        {theme.description}
                                      </Text>
                                    </Stack>
                                  </Stack>
                                </Card>
                              </UnstyledButton>
                            );
                          })}
                        </SimpleGrid>
                      </Stack>
                    );
                  })}
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="customise" pt="md">
                <Stack gap="xl">
                  <Stack gap="sm">
                    <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.7">
                      Theme colour
                    </Text>
                    <Stack gap="sm">
                      {THEME_COLOUR_FIELDS.map(field => (
                        <PaletteColourField
                          key={field.key}
                          label={field.label}
                          value={paletteDrafts[field.key]}
                          onChange={value => draftPaletteColour(field.key, value)}
                          onFocus={() => handleFieldFocus(field.key)}
                          onBlur={() => handleFieldBlur(field.key)}
                        />
                      ))}
                    </Stack>
                  </Stack>

                  <Stack gap="sm">
                    <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.7">
                      Shape colour
                    </Text>
                    <SegmentedControl
                      fullWidth
                      value={titlePageConfig.shapeColor}
                      onChange={value =>
                        updateConfig('shapeColor', value as BrandAssetBuilderShapeColour)
                      }
                      data={SHAPE_COLOUR_OPTIONS}
                    />
                  </Stack>

                  <Stack gap="sm">
                    <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.7">
                      Logo placement
                    </Text>
                    <SegmentedControl
                      fullWidth
                      value={logoPositionFromX(titlePageConfig.logoX) ?? ''}
                      onChange={value => {
                        const position = value as BrandAssetBuilderLogoPosition;
                        setData({
                          ...data,
                          titlePageConfig: {
                            ...titlePageConfig,
                            logoPosition: position,
                            logoX: LOGO_POSITION_PRESET_X[position],
                          },
                        });
                      }}
                      data={[
                        { value: 'left', label: 'Left' },
                        { value: 'center', label: 'Centre' },
                        { value: 'right', label: 'Right' },
                      ]}
                    />
                    <Box>
                      <Text size="xs" c="gray.6" mb={4}>
                        Logo size ({titlePageConfig.logoScale}%)
                      </Text>
                      <Slider
                        min={40}
                        max={160}
                        value={titlePageConfig.logoScale}
                        onChange={value => updateConfig('logoScale', value)}
                      />
                    </Box>
                    <Group grow align="flex-start">
                      <Box>
                        <Text size="xs" c="gray.6" mb={4}>
                          Horizontal position ({titlePageConfig.logoX}%)
                        </Text>
                        <Slider
                          min={10}
                          max={90}
                          value={titlePageConfig.logoX}
                          onChange={value => updateConfig('logoX', value)}
                        />
                      </Box>
                      <Box>
                        <Text size="xs" c="gray.6" mb={4}>
                          Vertical position ({titlePageConfig.logoY}%)
                        </Text>
                        <Slider
                          min={5}
                          max={95}
                          value={titlePageConfig.logoY}
                          onChange={value => updateConfig('logoY', value)}
                        />
                      </Box>
                    </Group>
                  </Stack>

                  <Switch
                    checked={titlePageConfig.hideDate}
                    onChange={event => updateConfig('hideDate', event.currentTarget.checked)}
                    label="Hide date"
                    description="Remove the date from the title page"
                  />
                </Stack>
              </Tabs.Panel>
            </Box>
          </Tabs>
        </Box>
      </Group>
    </DrawerFlowScreenLayout>
  );
}
